import React from 'react'
import type { Feedback } from '../../types'
import { FEEDBACK_CHANNELS, formatPeople, PERIOD_NAMES, PERIODS, plural, unratedText } from '../../utils'
import { PanelFreshness, PanelMeaning, PanelNoBenchmark, PanelNote, PanelSection, PanelTable } from '../AboutPanel'
import { PanelIncluded } from '../PanelIncluded'
import { CommentsView, FeedbackMode, ratedChannels, ratedPeople, ratedReviews, topWords } from './utils'

export const FEEDBACK_PANEL_TITLE = 'What your people say'

const working = (mode: FeedbackMode, minimum: number): string => {
  if (mode === 'none') return 'Ratings and comments will show here once people rate their coaching sessions and workshops. All feedback is anonymous.'
  if (mode === 'hidden') {
    return `We show a channel’s ratings and comments once at least ${minimum} different people have reviewed it, so nobody can be identified. All feedback is anonymous.`
  }
  return `All feedback is anonymous. A channel reviewed by fewer than ${minimum} different people is left out of the overall rating, the word cloud and the comments. A comment appears a little after its rating, once it has been prepared.`
}

type Props = { feedback: Feedback; mode: FeedbackMode; view: CommentsView }

export const FeedbackPanel = ({ feedback, mode, view }: Props) => (
  <>
    <PanelMeaning
      definition="Ratings and comments from short surveys after each coaching session and workshop. This card shows the average rating, how each channel compares and what people wrote."
      working={working(mode, feedback.minimum_reviews)}
    />
    <PanelSection heading="Who is included">
      <Included feedback={feedback} mode={mode} />
    </PanelSection>
    <PanelSection heading="The numbers behind the card">
      <PanelTable columns={['Period', 'Reviews', 'People', 'Average rating']} rows={periodRows(feedback, mode)} note={periodNote(mode, feedback.minimum_reviews)} />
    </PanelSection>
    <TopWords feedback={feedback} view={view} mode={mode} />
    <PanelSection heading="Where the numbers come from">
      <PanelFreshness source="Coaching ratings: from post-session surveys" at={null} />
      <PanelFreshness source="Workshop ratings: from post-workshop surveys" at={null} />
      <PanelFreshness source="Comments: shown a little after their rating, once prepared" at={null} />
    </PanelSection>
    <PanelNoBenchmark />
  </>
)

// A hidden channel's numbers stay out of the overall rating, so they are
// named in words rather than counted as exclusions.
const Included = ({ feedback, mode }: { feedback: Feedback; mode: FeedbackMode }) => {
  const minimum = feedback.minimum_reviews

  if (mode === 'none') {
    return <PanelIncluded included="No reviews" of="yet" exclusions={[]} everyone="Nobody has rated a coaching session or workshop yet." />
  }

  if (mode === 'hidden') {
    const waiting = `Everyone who has reviewed is counted. The results stay hidden until at least ${minimum} different people have reviewed coaching, or ${minimum} have reviewed workshops.`
    return <PanelIncluded included={plural(feedback.reviews, 'review')} of="so far" exclusions={[]} everyone={waiting} />
  }

  const rated = ratedChannels(feedback)
  const leftOut = FEEDBACK_CHANNELS.filter((channel) => !rated.includes(channel)).map(
    (channel) => `${unratedText(channel, feedback.channels[channel].reviews, minimum)}, so those reviews aren’t in the overall rating yet.`
  )
  const everyone = [`The overall rating counts every review of a channel at least ${minimum} different people have reviewed.`, ...leftOut].join(' ')

  return (
    <PanelIncluded
      included={plural(ratedReviews(feedback), 'review')}
      of={`from ${formatPeople(ratedPeople(feedback))}`}
      exclusions={[]}
      everyone={everyone}
    />
  )
}

// The periods count qualifying channels only, so before any channel
// qualifies they would all read "None": show the whole programme's total instead.
const periodRows = (feedback: Feedback, mode: FeedbackMode): string[][] => {
  if (mode === 'none') return [['Since the start', 'None yet', '', `Shows from ${feedback.minimum_reviews} people`]]
  if (mode === 'hidden') return [['Since the start', String(feedback.reviews), String(feedback.people), 'Hidden for now']]

  return PERIODS.map(({ key }) => {
    const { reviews, people: reviewers, average } = feedback.periods[key]
    if (reviews === 0) return [PERIOD_NAMES[key], 'None', '', '']
    return [PERIOD_NAMES[key], String(reviews), String(reviewers), average === null ? 'Hidden for now' : average.toFixed(1)]
  })
}

const periodNote = (mode: FeedbackMode, minimum: number): string => {
  if (mode === 'hidden') return `No channel has ${minimum} different reviewers yet, so this counts every review and shows no average.`
  return `Counts only channels at least ${minimum} different people have reviewed. A period has no average when fewer than ${minimum} people reviewed in it, or when comparing it with a longer period would single out fewer than ${minimum}.`
}

// The cloud's words with their counts, when the cloud is showing.
const TopWords = ({ feedback, view, mode }: { feedback: Feedback; view: CommentsView; mode: FeedbackMode }) => {
  if (mode !== 'ratings' || view !== 'cloud') return null

  const rows = topWords(feedback.keywords).map((keyword) => [keyword.word, plural(keyword.count, 'comment')])

  return (
    <PanelSection heading="Top words">
      <PanelTable
        columns={['Word or phrase', 'Comments']}
        rows={rows}
        note="Each count is the number of comments that used the word or phrase. Longer phrases look bigger in the cloud than short words used just as often."
      />
      <PanelNote>{`A word is orange when most of the comments that used it were rated 2 or lower. It needs ratings from at least ${feedback.minimum_reviews} different people first.`}</PanelNote>
    </PanelSection>
  )
}
